import angular from 'angular';
import _ from 'lodash-es';
import Utils from '~/scripts/services/utils';
import templateExportModal from '~/pages/template-export/modal/template-export-modal.html';
import templateExportModalController from '~/pages/template-export/modal/template-export-modal';
import APIV5 from '~/models/api';
import templateModalForm from '../../components/kform/modal/modal-form.html';
import ModalFormController from '../../components/kform/modal/modal-form.controller';

/**
 * @typedef {Object} UIActionServiceInstance
 * @property {Function} action
 * @property {Object<string, Function>} methods
 */

/* @ngInject */
export const UIActionService = (
	/** @type {import('angular').IHttpService} */ $http,
	/** @type {import('angular').ILocationService} */ $location,
	/** @type {import('angular').IQService} */ $q,
	/** @type {import('angular').IRootScopeService} */ $rootScope,
	/** @type {import('angular').ui.bootstrap.IModalService} */ $uibModal,
	/** @type {import('~/scripts/providers/models/notification').NotificationService} */ Notification,
) => {
	/**
	 * @type {import('@knitiv/api-client-javascript').KNode}
	 */
	let localMethodKid;

	/**
	 * @type {import('@knitiv/api-client-javascript').KNode}
	 */
	let localNodeKid;

	/**
	 * @type {any}
	 */
	let localData;

	/**
	 * @type {Record<string, import('@knitiv/api-client-javascript').UIActionOpenForm['params']>}
	 */
	const uiActions = {};

	const checkForKeywords = (/** @type {any} */ parameters) => {
		const newParameters = parameters;
		for (const [k, parameter] of Object.entries(newParameters)) {
			// eslint-disable-next-line unicorn/prefer-switch
			if (parameter === '__SELF__') {
				newParameters[k] = localMethodKid;
			}
			else if (parameter === '__KID__') {
				newParameters[k] = localNodeKid;
			}
			else if (parameter === '__DATA__') {
				newParameters[k] = localData;
			}
		}
		return newParameters;
	};

	// noinspection JSUnusedGlobalSymbols
	/** @type {any} */
	const methods = {
		/**
		 * @param {import('@knitiv/api-client-javascript').UIActionCallAPI['params']} requestParameters
		 * @returns {any}
		 */
		callAPI: (/** @type {import('@knitiv/api-client-javascript').UIActionCallAPI['params']} */ requestParameters) => {
			const parameters = {
				...requestParameters,
				...checkForKeywords(requestParameters.params),
			};
			const { action } = parameters;
			delete parameters.action;
			return APIV5.instance.raw(
				action,
				parameters,
				undefined,
				{ id: 'method-service.call-api' },
			);
		},

		/**
		 * @param {import('@knitiv/api-client-javascript').UIActionConsoleLog['params']} parameters
		 * @returns {any}
		 */
		consoleLog: parameters => {
			// eslint-disable-next-line no-console
			console.log(parameters);
			return $q.resolve(true);
		},

		/**
		 * @param {import('@knitiv/api-client-javascript').UIActionDownload['params']} parameters
		 * @returns {any}
		 */
		download: parameters => {
			if ('data' in parameters) {
				Utils.download(parameters.data, parameters.type, parameters.name);
				return $q.resolve(true);
			}
			if ('fileid' in parameters) {
				const url = APIV5.instance.downloadURL({
					nodeid: parameters.fileid,
				});
				return window.open(url, '_self');
			}
			if ('kid' in parameters) {
				const url = APIV5.instance.downloadURL({
					nodeid: parameters.kid,
				});
				return window.open(url, '_self');
			}
			if ('url' in parameters) {
				return $http.get(parameters.url);
			}

			return $q.reject();
		},

		/**
		 * @param {import('@knitiv/api-client-javascript').UIActionKnmExec['params']} parameters
		 * @returns {any}
		 */
		knmExec: parameters => {
			const newParameters = checkForKeywords(parameters);
			return APIV5.instance.knmExecute(
				{
					method_kid: newParameters.method_kid,
					node_kid:   newParameters.node_kid,
					params:     newParameters.params,
				},
				{ id: 'method-service.knm-exec' },
			);
		},

		/**
		 * @param {import('@knitiv/api-client-javascript').UIActionLocationPath['params']} parameters
		 * @returns {any}
		 */
		locationPath: parameters => {
			$location.path(parameters.path);
			return $q.resolve(true);
		},

		/**
		 * @param {import('@knitiv/api-client-javascript').UIActionLocationUrl['params']} parameters
		 * @returns {any}
		 */
		locationUrl: parameters => {
			$location.url(parameters.url);
			return $q.resolve(true);
		},

		/**
		 * @param {import('@knitiv/api-client-javascript').UIActionNotification['params']} parameters
		 * @returns {any}
		 */
		notification: parameters => {
			Notification(parameters);
		},

		/**
		 * @param {import('@knitiv/api-client-javascript').UIActionOpenForm['params']} parameters
		 * @returns {any}
		 */
		openForm: parameters => {
			const {
				options,
				tab,
			} = parameters;
			const uuid = Utils.guid();
			uiActions[uuid] = parameters;
			$location.url(`/form/${options.node_kid}${options.create ? '/create' : ''}`)
				.search({
					uiActionOpenForm: uuid,
					tab,
				});
		},

		/**
		 * @param {import('@knitiv/api-client-javascript').UIActionOpenModalForm['params']} parameters
		 * @returns {any}
		 */
		openModalForm: parameters => {
			const modalOptions = {
				...parameters.options,
				ariaLabelledBy:  'modal-form-title',
				ariaDescribedBy: 'modal-form-body',
				template:        templateModalForm,
				controller:      ModalFormController,
				controllerAs:    '$ctrl',
				resolve:         {
					form:       () => parameters.form,
					submission: () => parameters.submission || { data: {} },
					options:    () => parameters.options || {},
				},
			};
			return $uibModal.open(modalOptions).result;
		},

		/**
		 * @returns {any}
		 */
		refreshForm: () => {
			$rootScope.$broadcast('Form.refresh');
			return $q.resolve(true);
		},

		/**
		 * @returns {any}
		 */
		templateExport: () => {
			const { result } = $uibModal.open({
				template: templateExportModal,
				size: 'lg',
				controller: templateExportModalController,
				windowClass: 'modal-form-select',
				controllerAs: '$ctrl',
				resolve: {
					node() {
						return localNodeKid;
					},
				},
			});

			return result
				.then(newResult => $q.when(
					APIV5.instance.kdgGenerate({
						format: 'PDF',
						node_kid: localNodeKid,
						template_kid: newResult.kid,
					}),
				))
				.catch(error => {
					if (error !== 'cancel') {
						console.error('e', error);
					}
				});
		},

		/**
		 * @param {import('@knitiv/api-client-javascript').UIActionTriggerEvent['params']} parameters
		 * @returns {any}
		 */
		triggerEvent: parameters => {
			$rootScope.$broadcast(parameters.event, parameters.params);
			return $q.resolve(true);
		},

		/**
		 * @param {import('@knitiv/api-client-javascript').UIActionFormUpdateCurrentValue['params']} parameters
		 * @returns {any}
		 */
		updateCurrentValue: parameters => $q.resolve({
			action: 'updateCurrentValue',
			value: parameters,
		}),

		/**
		 * @param {import('@knitiv/api-client-javascript').UIActionFormUpdateData['params']} parameters
		 * @returns {any}
		 */
		updateFormData: parameters => $q.resolve({ action: 'updateFormData', data: parameters }),
	};

	// eslint-disable-next-line prefer-const
	const localAction = (
		/** @type {string} */ action,
		/** @type {any} */ parameters,
		/** @type {any} */ methodKid,
		/** @type {any} */ nodeKid,
		/** @type {any} */ data,
		// eslint-disable-next-line unicorn/prevent-abbreviations
		/** @type {any} */ def,
	) => {
		const deferred = def || $q.defer(); // Use def when it is recursive call

		localMethodKid = methodKid;
		localNodeKid = nodeKid;
		localData = data;

		if (action === 'knmExec') {
			_.merge(parameters, { node_kid: nodeKid }, parameters);
		}

		if (typeof methods[action] === 'function') {
			methods[action](parameters)
				.then(
					(/** @type {any} */ returnValue) => {
						if (typeof returnValue === 'object' && 'front' in returnValue) {
							for (const r of Object.values(returnValue.front)) {
								localAction(r.action, r.params, methodKid, nodeKid, r.data || data, deferred);
							}
						}
						else {
							deferred.resolve(returnValue);
						}
						return true;
					},
				).catch((/** @type {any} */ error) => {
					console.error(error);
					return deferred.reject('Error in action');
				});
		}
		else {
			deferred.reject('Unknown action');
		}

		return deferred.promise;
	};

	return {
		action: localAction,
		methods,
		uiActions,
	};
};

angular.module('app')
	.factory('UIActionService', UIActionService)
